import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import { FiRefreshCw, FiHeart, FiDroplet, FiMoon, FiSun, FiWind, FiCoffee } from 'react-icons/fi';

const TIPS = [
  {
    title: "The Hydration Multiplier",
    text: "Drinking 500ml of water can temporarily boost metabolism by 24-30% for up to 90 minutes. Aim for 3-4 liters daily.",
    icon: FiDroplet,
    color: "text-blue-500"
  },
  {
    title: "Sleep & Leptin",
    text: "Less than 7 hours of sleep disrupts leptin and ghrelin, increasing hunger signals by up to 25% the following day.",
    icon: FiMoon,
    color: "text-indigo-500"
  },
  {
    title: "NEAT Over Gym",
    text: "Non-Exercise Activity Thermogenesis (walking, fidgeting) often burns more calories than a 1-hour workout. Keep moving!",
    icon: FiHeart,
    color: "text-red-500"
  },
  {
    title: "Protein's TEF",
    text: "Protein has a 20-30% Thermic Effect of Food (TEF), meaning your body burns 30 calories just to process 100 calories of protein.",
    icon: FiSun,
    color: "text-orange-500"
  },
  {
    title: "Circadian Fasting",
    text: "Try to stop eating 3 hours before bed. This aligns your insulin sensitivity with your natural circadian rhythm.",
    icon: FiWind,
    color: "text-emerald-500"
  },
  {
    title: "Caffeine Timing",
    text: "Delay your first coffee by 90 minutes after waking to allow adenosine clearance and prevent the afternoon crash.",
    icon: FiCoffee,
    color: "text-amber-700"
  }
];

const HealthTips = () => {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    // Initial random tip
    setIndex(Math.floor(Math.random() * TIPS.length));
  }, []);

  const nextTip = () => {
    setIndex((prev) => (prev + 1) % TIPS.length);
  };

  const currentTip = TIPS[index];

  return (
    <section className="py-12 border-t border-gray-100 dark:border-gray-800">
      <div className="bg-white dark:bg-gray-800/40 rounded-[2rem] p-8 border border-gray-100 dark:border-gray-700 shadow-sm relative overflow-hidden">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full bg-blue-600 animate-pulse" />
            <span className="text-[10px] font-black uppercase tracking-[0.3em] text-gray-400">Pro Health Intelligence</span>
          </div>
          <button 
            onClick={nextTip}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors group"
          >
            <SafeIcon icon={FiRefreshCw} className="w-4 h-4 text-gray-400 group-hover:rotate-180 transition-transform duration-500" />
          </button>
        </div>

        <AnimatePresence mode="wait">
          <motion.div
            key={index}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="flex flex-col md:flex-row gap-6 items-center md:items-start text-center md:text-left"
          >
            <div className={`w-16 h-16 rounded-2xl bg-gray-50 dark:bg-gray-900 flex items-center justify-center flex-shrink-0`}>
              <SafeIcon icon={currentTip.icon} className={`w-8 h-8 ${currentTip.color}`} />
            </div>
            <div>
              <h4 className="text-xl font-black tracking-tight mb-2 dark:text-white">{currentTip.title}</h4>
              <p className="text-gray-500 dark:text-gray-400 leading-relaxed max-w-xl">
                {currentTip.text}
              </p>
            </div>
          </motion.div>
        </AnimatePresence>
        
        {/* Decorative background element */}
        <div className="absolute -bottom-6 -right-6 opacity-[0.03] dark:opacity-[0.05] pointer-events-none">
          <SafeIcon icon={currentTip.icon} className="w-32 h-32" />
        </div>
      </div>
    </section>
  );
};

export default HealthTips;